import { useMemo, useRef } from 'react';
import { InfiniteData, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { atom } from 'jotai';

import { showToast } from '@restworld/ui-ds-base';
import { IndexInvoices, useEmployersService } from '@restworld/data-services';
import { ApiError } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';

// Backend `error_code`s for invoices that can't be paid manually by card/PayPal
// because they are already bound to a SEPA payment. Kept in sync with the
// core-services `Invoice.create_checkout_session/1` guard. Each maps to its own
// translation key so the user gets an accurate message.
const PAY_INVOICE_ERROR_MESSAGE_KEYS: Record<string, string> = {
  // A SEPA debit charge is actively clearing (PaymentIntent `processing`).
  payment_processing: 'payment_still_processing',
  // SEPA is the customer's default and is already attached to the invoice.
  sepa_payment_pending: 'invoice_paid_by_sepa',
};

/**
 * Translation key (namespace `payment-and-invoices`) for a failed checkout session
 * request: the dedicated message for a known backend `error_code`, otherwise the
 * generic one.
 */
export const getPayInvoiceErrorMessageKey = (error: ApiError): string => {
  const errorCode = (error?.data as { error_code?: string } | undefined)?.error_code;
  return (errorCode && PAY_INVOICE_ERROR_MESSAGE_KEYS[errorCode]) || 'something_went_wrong';
};

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 10;
export const fetchInvoicesQueryKey = ['invoices-list'];
export const useFetchInvoicesQuery = (params?: { limit?: number }) => {
  const { limit = DEFAULT_LIMIT } = params || {};
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const employersService = useEmployersService();
  const nextPageRef = useRef<number>(undefined);
  const hasNextPageRef = useRef<boolean>(undefined);

  const query = useInfiniteQuery({
    queryKey: fetchInvoicesQueryKey,
    queryFn: async ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return (
        await employersService.fetchInvoices({
          limit,
          offset: pageParam * limit,
          organizationId,
        })
      ).data;
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.hits;
      const invoicesLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = invoicesLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
    enabled: !!organizationId,
  });
  const employerUserContactsInfo = useMemo(() => extractData(query.data), [query.data]);
  return {
    invoicesList: employerUserContactsInfo?.data,
    hits: employerUserContactsInfo?.hits,
    hasNextPage: hasNextPageRef.current,
    isLoading: query.isLoading,
    query,
  };
};

export function extractData(data?: InfiniteData<IndexInvoices, unknown>): IndexInvoices {
  if (!data || !data.pages) return { data: [], hits: 0 };
  return { data: data.pages.map((page) => page.data).flat(), hits: data.pages[0].hits };
}
export const invoiceIdAtom = atom<number>(null);

export const usePayInvoiceMutation = () => {
  const { t } = useTranslation('payment-and-invoices');
  const employersService = useEmployersService();
  return useMutation({
    mutationKey: ['pay-invoice'],
    mutationFn: async ({ id }: { id: number }) => (await employersService.createCheckoutSession(id)).data,
    onSuccess({ url }) {
      if (url) window.location.href = url;
    },
    onError(error: ApiError, variables) {
      console.error(`Error creating checkout session for invoice ${variables.id}`, error);
      showToast(t(getPayInvoiceErrorMessageKey(error)), 'error', {
        position: 'top-right',
        autoClose: 5000,
      });
    },
  });
};

export const useDownloadInvoiceMutation = () => {
  const employersService = useEmployersService();
  return useMutation({
    mutationKey: ['download-invoice'],
    mutationFn: async ({ id }: { id?: number; invoiceNumber?: number }) =>
      (await employersService.fetchInvoicePdf({ invoiceId: id })).data,
    onSuccess(data, { invoiceNumber }) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `restworld-invoice-${invoiceNumber}.pdf`);
      // Force download
      link.click();
      // Clean up and remove the link
      link?.parentNode?.removeChild(link);
    },
    onError(error, variables) {
      console.error(`Error downloading invoice for ${variables.id}`, error);
    },
  });
};
